import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import i18next from 'i18next';
import dayjs from 'dayjs';

import axiosInstance from '../../config/axiosConfig';
import { Toast } from 'primereact/toast';
import CampaignTable from '../Tables/CampaignTable';
import EmptyBoxContent from '../../Utils/EmptyBoxContent';

import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';
import useCampaignStore from '../../store/useCampaignStore';
import usePaginationStore from '../../store/usePaginationStore';

const t = (i18nKey, params) => i18next.t(`userDashboard.campaigns.${i18nKey}`, params);

const Campaigns = () => {
    const [loading, setLoading] = useState(true);
    const [campaignsList, setCampaignsList] = useState([]);
    const [isReloadDisabled, setIsReloadDisabled] = useState(false);
    const toast = useRef(null);

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);
    const { campaigns, setCampaigns, clearCampaigns } = useCampaignStore();

    const {
        currentPage,
        limitFilter,
        setTotalItems,
        PaginationNavBarQuery,
        paginationLoading,
        setPaginationLoading,
    } = usePaginationStore();

    const fetchData = useCallback(async (force = false) => {
        if (!selectedCompany) return;

        setPaginationLoading(true);
        setLoading(true);

        try {
            const response = await axiosInstance.get(`/dashboard/campaign/${selectedCompany.company_id}/list`, {
                params: {
                    page: currentPage,
                    limit: limitFilter,
                    query: PaginationNavBarQuery,
                },
            });

            const items = response.data.items || [];
            if (!items.length) return setCampaignsList([]);

            const campaigns_items = items.map((item) => ({
                campaign_id: item.campaign_id,
                campaign_name: item.campaign_name,
                language: item.language === 'fr' ? '🇫🇷 FR' : '🇬🇧 EN',
                recruiter: item.recruiter,
                has_questions: item.questions.length > 0,
                status: item.status,
                candidates_count: item.candidates_count || 0,
                created_at: dayjs(item.created_at).format('DD/MM/YYYY - HH:mm'),
                fetched_at: dayjs(new Date).format('DD/MM/YYYY - HH:mm'),
                timestamp: new Date().getTime(),
            }));

            setCampaignsList(campaigns_items);
            if (currentPage === 1 && !PaginationNavBarQuery) {
                setCampaigns(campaigns_items);
            }
            setTotalItems(response.data.count || 0);

        } catch (error) {
            console.error(error);
            toast.current.show({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors de la récupération des campagnes, veuillez réessayer plus tard',
                life: 10000,
            });
            setCampaignsList([]);
            clearCampaigns();
        } finally {
            setLoading(false);
            setPaginationLoading(false);
        }
    }, [selectedCompany, currentPage, limitFilter, PaginationNavBarQuery, setCampaigns, clearCampaigns, setTotalItems, setPaginationLoading]);

    useEffect(() => {
        fetchData();
    }, [currentPage, limitFilter, PaginationNavBarQuery]);

    const handleReloadCampaign = () => {
        if (loading || isReloadDisabled) return;
        setIsReloadDisabled(true);
        clearCampaigns();
        setCampaigns([]);
        setLoading(true);

        fetchData(true);

        clearTimeout(window.reloadTimeout);
        window.reloadTimeout = setTimeout(() => {
            setIsReloadDisabled(false);
        }, 60000);
    };

    return (
        <section className='home'>
            <Toast ref={toast} position="bottom-right" />
            <div className="container">
                <div className="columns is-vcentered is-multiline">

                    <div className="column">
                        <p>
                            <span className="icon has-text-success"><i className="fas fa-check-circle"></i></span>
                            {campaignsList[campaignsList?.length - 1]?.fetched_at ? <span>{t('updated_at')} {campaignsList[campaignsList?.length - 1]?.fetched_at} - </span> : <span>{t('updated_at')} {dayjs(Date.now()).format('DD/MM/YYYY - HH:mm')} - </span>}
                            <a className={clsx('has-text-link', { 'is-unselectable has-text-grey-light': isReloadDisabled || loading })}
                                onClick={handleReloadCampaign} disabled={isReloadDisabled}
                            >{t('reload')}</a>
                        </p>
                    </div>

                    <div className="column has-text-right">
                        <a className='d-button is-size-6 p-2' href="/dashboard/campaigns/create">{t('new_campaign_btn')}</a>
                    </div>

                </div>

                {!loading && !campaignsList.length && (
                    <EmptyBoxContent
                        icon={PaginationNavBarQuery !== '' ? "fa-solid fa-magnifying-glass empty-icon" : "fa fa-bullhorn empty-icon"}
                        title={PaginationNavBarQuery !== '' ? "Aucune campagne trouvée" : "Vous n'avez pas encore de campagne"}
                        content={PaginationNavBarQuery !== '' ? "Essayez d'ajuster votre recherche ou de créer une nouvelle campagne." : "Afin d'envoyer vos premiers tests à des participants, vous devez créer une campagne."}
                        btn1Text="Retour Au Dashboard"
                        btn2Text="Créer une campagne"
                        btn1Href="/dashboard/"
                        btn2Href="/dashboard/campaigns/create"
                    />
                )}

                <CampaignTable
                    data={campaignsList}
                    dataCount={campaignsList.length}
                    loading={loading}
                />
            </div>
        </section>
    );
};

export default Campaigns;